import {
  BookingActionState,
  bookingStateLabelKey as resolveBookingStateLabelKey,
  hasActiveOperation as bookingHasActiveOperation,
} from '@frontend/features/travel-services/bookingState';

import { isRuteroCartItemType } from './components/CartItem/helper';

export interface RuteroCartAuthorityItem {
  id?: string | null;
  type: string;
}

export interface RuteroCartQueryableAuthorityItem
  extends RuteroCartAuthorityItem {
  id: string;
}

export interface RuteroCartAuthorityCoverage {
  ruteroItems: RuteroCartAuthorityItem[];
  queryableRuteroItems: RuteroCartQueryableAuthorityItem[];
  hasUnaddressableRuteroItem: boolean;
}

export const RUTERO_CART_BOOKING_STATE_COLORS: Record<
  string,
  'default' | 'info' | 'success' | 'error'
> = {
  draft: 'default',
  quoted: 'info',
  held: 'info',
  confirmed: 'success',
  cancelled: 'error',
  failed: 'error',
};

export interface RuteroCartBookingAuthorityEntry<
  TBooking extends BookingActionState & {
    providerStatus?: string;
    expiresAt?: string;
  } = BookingActionState & {
    providerStatus?: string;
    expiresAt?: string;
  },
> {
  itemId: string;
  booking?: TBooking;
  isLoading: boolean;
  isError: boolean;
}

export interface RuteroCartAuthorityEvaluation {
  isLoadingAuthority: boolean;
  hasAuthorityError: boolean;
  hasUnaddressableRuteroItem: boolean;
  hasUnknownProviderState: boolean;
  hasActiveOperation: boolean;
  isAuthorityResolved: boolean;
  checkoutTemporarilyBlocked: boolean;
}

export function evaluateRuteroCartAuthority<
  TBooking extends BookingActionState & {
    providerStatus?: string;
    expiresAt?: string;
  },
>(
  entries: RuteroCartBookingAuthorityEntry<TBooking>[],
  options: { hasUnaddressableRuteroItem?: boolean } = {},
): RuteroCartAuthorityEvaluation {
  const hasUnaddressableRuteroItem =
    options.hasUnaddressableRuteroItem === true;
  const isLoadingAuthority = entries.some((entry) => entry.isLoading);
  const hasAuthorityError = entries.some(
    (entry) => entry.isError || (!entry.isLoading && !entry.booking),
  ) || hasUnaddressableRuteroItem;
  const hasUnknownProviderState = entries.some(
    (entry) => entry.booking?.unknownProviderState === true,
  );
  const hasActiveOperation = entries.some((entry) =>
    entry.booking ? bookingHasActiveOperation(entry.booking) : false,
  );
  const isAuthorityResolved = !isLoadingAuthority && !hasAuthorityError;

  return {
    isLoadingAuthority,
    hasAuthorityError,
    hasUnaddressableRuteroItem,
    hasUnknownProviderState,
    hasActiveOperation,
    isAuthorityResolved,
    checkoutTemporarilyBlocked:
      isLoadingAuthority ||
      hasAuthorityError ||
      hasUnknownProviderState ||
      hasActiveOperation,
  };
}

export function getRuteroCartRowPresentation<
  TBooking extends BookingActionState & {
    providerStatus?: string;
    expiresAt?: string;
  },
>(
  entry: RuteroCartBookingAuthorityEntry<TBooking> | undefined,
  options: { hasUsableItemId?: boolean } = {},
):
  | { kind: 'loading'; messageKey: string }
  | { kind: 'unavailable'; messageKey: string }
  | {
      kind: 'booking';
      booking: TBooking;
      bookingStateLabelKey: string;
      showUnknownProviderWarning: boolean;
      showActiveOperation: boolean;
      showHoldExpiry: boolean;
    } {
  if (options.hasUsableItemId === false) {
    return {
      kind: 'unavailable',
      messageKey: 'cart.ruteroAuthority.rowUnavailable',
    };
  }

  if (!entry || entry.isLoading) {
    return {
      kind: 'loading',
      messageKey: 'cart.ruteroAuthority.rowVerifying',
    };
  }

  if (entry.isError || !entry.booking) {
    return {
      kind: 'unavailable',
      messageKey: 'cart.ruteroAuthority.rowUnavailable',
    };
  }

  return {
    kind: 'booking',
    booking: entry.booking,
    bookingStateLabelKey: resolveBookingStateLabelKey(
      entry.booking.state,
      entry.booking.providerStatus,
    ),
    showUnknownProviderWarning: entry.booking.unknownProviderState === true,
    showActiveOperation: bookingHasActiveOperation(entry.booking),
    showHoldExpiry:
      entry.booking.state === 'held' && Boolean(entry.booking.expiresAt),
  };
}

export function classifyRuteroCartAuthorityItems<
  TItem extends RuteroCartAuthorityItem,
>(items: TItem[]): RuteroCartAuthorityCoverage {
  const ruteroItems = items.filter((item) => isRuteroCartItemType(item.type));
  const queryableRuteroItems =
    ruteroItems.reduce<RuteroCartQueryableAuthorityItem[]>((result, item) => {
      if (typeof item.id === 'string' && item.id.trim().length > 0) {
        result.push({ ...item, id: item.id });
      }
      return result;
    }, []);

  return {
    ruteroItems,
    queryableRuteroItems,
    hasUnaddressableRuteroItem:
      queryableRuteroItems.length !== ruteroItems.length,
  };
}

export function formatRuteroCartCommercialDisplay(
  price: number,
  currency?: string,
): string {
  const amount = Number(price).toFixed(2);
  return currency ? `${currency} ${amount}` : amount;
}
